import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import ToolShell from '../../components/ToolShell';
import { Loader2, ArrowRight, Copy, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SECURITY_HEADERS = [
  'strict-transport-security',
  'content-security-policy',
  'x-frame-options',
  'x-content-type-options',
  'referrer-policy',
  'permissions-policy',
  'cross-origin-opener-policy',
  'cross-origin-embedder-policy',
  'cross-origin-resource-policy',
];

export default function BulkHttpHeaderCheck() {
  const [raw, setRaw] = useState('');
  const [filter, setFilter] = useState('');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [copied, setCopied] = useState(false);

  const urls = useMemo(() =>
    raw.split(/\r?\n/).map((s) => s.trim()).filter(Boolean).slice(0, 50)
  , [raw]);

  const run = async () => {
    if (urls.length === 0) { toast.error('Paste at least one URL'); return; }
    setRunning(true);
    setResults([]);
    try {
      const { data } = await axios.post(
        `${API}/api/tools/http-probe`,
        { urls, follow_redirects: false, method: 'HEAD' },
        { timeout: 75000 }
      );
      setResults(data.results || []);
      toast.success(`Inspected ${data.count} URL${data.count === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Inspection failed');
    } finally {
      setRunning(false);
    }
  };

  const filterText = filter.trim().toLowerCase();
  const matchesFilter = (k) => !filterText || k.toLowerCase().includes(filterText);

  const exportCsv = async () => {
    if (results.length === 0) return;
    // Build a column-wise CSV with all encountered headers
    const allHeaders = new Set();
    for (const r of results) {
      if (r.ok && r.hops?.[0]?.headers) Object.keys(r.hops[0].headers).forEach((k) => allHeaders.add(k));
    }
    const sortedHeaders = [...allHeaders].sort();
    const rows = [['URL', 'Status', ...sortedHeaders]];
    for (const r of results) {
      if (!r.ok) { rows.push([r.url, r.error || 'error', ...sortedHeaders.map(() => '')]); continue; }
      const h = r.hops?.[0]?.headers || {};
      rows.push([r.url, String(r.hops?.[0]?.status ?? ''), ...sortedHeaders.map((k) => h[k] || '')]);
    }
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    try {
      await navigator.clipboard.writeText(csv);
      setCopied(true);
      toast.success('CSV copied to clipboard');
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Could not access clipboard'); }
  };

  return (
    <ToolShell
      slug="bulk-http-header-check"
      title="Bulk HTTP Header Checker: Free SEO Audit | Fantastic Forms"
      h1="Bulk HTTP Header Check"
      tagline="Paste up to 50 URLs and we'll fetch the response headers for each. Perfect for auditing security headers, cache policies, server signatures, and CDN behaviour across an entire site at once."
    >
      <div className="rounded-xl border border-zinc-200 bg-white p-6 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="hh-input" className="text-sm">URLs (one per line, max 50)</Label>
          <Textarea
            id="hh-input"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={`https://yoursite.com\nhttps://yoursite.com/pricing\nhttps://api.yoursite.com`}
            rows={8}
            className="font-mono text-xs"
            data-testid="hh-input"
          />
          <p className="text-[11px] text-zinc-500">{urls.length} URL{urls.length === 1 ? '' : 's'} detected.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button
            onClick={run}
            disabled={running || urls.length === 0}
            className="bg-[#FE6A6B] hover:bg-[#e85f60] text-white"
            data-testid="hh-run"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ArrowRight className="w-4 h-4 mr-1.5" />}
            {running ? 'Inspecting…' : 'Fetch headers'}
          </Button>
          <Input
            placeholder="Filter headers (e.g. content, security, cache)…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="hh-filter"
          />
        </div>
        {results.length > 0 && (
          <div>
            <Button variant="outline" onClick={exportCsv} data-testid="hh-copy">
              {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
              Copy as CSV
            </Button>
          </div>
        )}
      </div>

      {results.length > 0 && (
        <div className="space-y-3" data-testid="hh-results">
          {results.map((r, i) => {
            const h = r.ok ? (r.hops?.[0]?.headers || {}) : {};
            const status = r.ok ? r.hops?.[0]?.status : null;
            const entries = Object.entries(h).filter(([k]) => matchesFilter(k));
            const securityPresent = SECURITY_HEADERS.filter((k) => h[k]);
            const securityMissing = SECURITY_HEADERS.filter((k) => !h[k]);
            return (
              <div key={i} className="rounded-xl border border-zinc-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <p className="font-mono text-xs text-zinc-700 break-all flex-1 min-w-0">{r.url}</p>
                  {status && (
                    <span className={`shrink-0 inline-flex items-center justify-center min-w-[3rem] h-6 rounded text-xs font-medium font-mono px-2 ${
                      status >= 200 && status < 300 ? 'bg-emerald-50 text-emerald-700' :
                      status >= 300 && status < 400 ? 'bg-amber-50 text-amber-700' :
                      'bg-red-50 text-red-700'
                    }`}>{status}</span>
                  )}
                </div>
                {!r.ok ? (
                  <p className="text-sm text-red-600 inline-flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> {r.message || r.error}</p>
                ) : (
                  <>
                    {(filterText === '') && (
                      <div className="mb-3 flex flex-wrap gap-1.5">
                        {securityPresent.map((k) => (
                          <span key={k} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700">✓ {k}</span>
                        ))}
                        {securityMissing.slice(0, 4).map((k) => (
                          <span key={k} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-100 text-zinc-500">⊘ {k}</span>
                        ))}
                      </div>
                    )}
                    <div className="border-t border-zinc-100 pt-3">
                      <table className="w-full text-xs">
                        <tbody>
                          {entries.length === 0 ? (
                            <tr><td className="text-zinc-400 italic">No headers match the filter.</td></tr>
                          ) : entries.map(([k, v]) => (
                            <tr key={k} className="align-top">
                              <td className="text-zinc-500 font-mono pr-3 py-0.5 whitespace-nowrap">{k}</td>
                              <td className="font-mono break-all py-0.5">{v}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <section className="prose prose-sm max-w-none text-zinc-700">
        <h2 className="text-lg font-semibold text-zinc-900 mt-2">Why response headers matter</h2>
        <ul>
          <li><strong>Security posture</strong>: HSTS, CSP, X-Frame-Options and friends are the cheapest defences against the most common web attacks.</li>
          <li><strong>Cache & CDN behaviour</strong>: <code>Cache-Control</code>, <code>CDN-Cache-Control</code>, and <code>Age</code> tell you whether your assets are actually being cached.</li>
          <li><strong>Server fingerprint</strong>: leaked <code>Server</code> or <code>X-Powered-By</code> headers reveal your stack to attackers.</li>
          <li><strong>Content-Type drift</strong>: silent mismatches (e.g. JSON served as <code>text/plain</code>) break front-end clients.</li>
        </ul>
        <h3 className="text-base font-semibold text-zinc-900 mt-4">Security-header chips</h3>
        <p>
          For each URL we highlight 9 standard security headers (green chips for present, grey chips for missing). Filter by typing in the search box (e.g. "cache", "security", "content").
        </p>
        <p className="text-zinc-500">Each URL is fetched once with a HEAD request (no body, no JS execution). URLs pointing to internal/private IPs are blocked. Max 50 URLs per request.</p>
      </section>
    </ToolShell>
  );
}
